import React from 'react';
import { ActionButton, DefaultButton, IButtonStyles, IconButton, Label, MessageBar, MessageBarType, Stack, Text, TooltipHost } from '@fluentui/react';
import { ITenant } from '../../data-structures/interfaces';
import { ScopeType } from '../../data-structures/enums';
import { LanguageContext } from '../../utilities/LocalizationModule';
import { labelHeadingStyle } from '../../styles/PanelStyle';
import { CopilotConsentStatus, getCopilotConsentStatus, parseCopilotCheckedAt } from '../../utilities/helpers/copilotConsentStatus';
import { adminConsentPageUrl, openAdminConsentPage } from '../../utilities/helpers/adminConsent';
import CopilotPromptsPanel from './CopilotPromptsPanel';

interface ICopilotSettingsSectionProps {
	tenant: ITenant;
}

/** How long Copy link says "Link copied" before it offers to copy again. */
export const LINK_COPIED_MS = 3000;

const statusBarTypes: Record<CopilotConsentStatus, MessageBarType> = {
	granted: MessageBarType.success,
	missing: MessageBarType.warning,
	unknown: MessageBarType.info,
};

const mutedText = { color: 'var(--db-fg-muted)' };

// The details toggle sits flush with the section's left edge, like a heading you can open.
const detailsToggleStyles: Partial<IButtonStyles> = {
	root: { height: 28, padding: 0, alignSelf: 'flex-start' },
	icon: { fontSize: 12, marginLeft: 0 },
	label: { margin: '0 0 0 4px' },
};

// Copies text to the clipboard: the Clipboard API first; where it is missing or refused (an older
// browser, an insecure context, a denied permission), the browser's older copy command on a hidden
// text area inside `host`: the Edit tenant panel traps focus, and a text area outside it would
// let the trap move focus to the panel's Close button. False when neither worked.
const copyToClipboard = async (text: string, host: HTMLElement): Promise<boolean> => {
	try {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		// Fall through to the copy command.
	}
	const area = document.createElement('textarea');
	try {
		area.value = text;
		area.setAttribute('readonly', '');
		area.style.position = 'fixed';
		area.style.opacity = '0';
		host.appendChild(area);
		area.select();
		return document.execCommand('copy') === true;
	} catch {
		return false;
	} finally {
		area.remove();
	}
};

/**
 * Edit Tenant → Copilot (docs/superpowers/specs/2026-10-13-copilot-chat-design.md §4, phase 2).
 *
 * Nothing here is a tenant setting. The status is what CopilotConsentRecorder last saw in one of the
 * tenant's Graph tokens, and consent itself is granted at Microsoft. So the section reads the tenant
 * as the panel opened it, never touches the draft, and never arms Save.
 *
 * Consent is the tenant's only switch for Copilot (there is no per-tenant flag). That is why the
 * details say plainly that it widens every Docubird user's Graph token, and how to revoke it. They
 * start collapsed: the status and the two actions are what an admin comes here for.
 *
 * The consent actions (Grant, and Copy link for a partner to send the page to the customer's
 * administrator) are always offered: there is no dated consent gate (decided 2026-10-19). The
 * section is visible to everyone who can edit the tenant. There is no privacy statement link:
 * customers already know the feature and consent to it (decided 2026-10-19).
 */
const CopilotSettingsSection: React.FC<ICopilotSettingsSectionProps> = ({ tenant }) => {
	const languageStrings = React.useContext(LanguageContext);
	const [copyResult, setCopyResult] = React.useState<'copied' | 'failed'>();
	const sectionRef = React.useRef<HTMLDivElement>(null);
	const copyButtonRef = React.useRef<HTMLButtonElement>(null);
	const [isDetailsOpen, setIsDetailsOpen] = React.useState<boolean>(false);
	const [isPromptsPanelOpen, setIsPromptsPanelOpen] = React.useState<boolean>(false);
	const detailsId = `copilot-details-${React.useId().replace(/:/g, '')}`;

	// "Link copied" is a moment's confirmation, not a state: the button offers to copy again after it.
	React.useEffect(() => {
		if (copyResult !== 'copied') return undefined;
		const timer = window.setTimeout(() => setCopyResult(undefined), LINK_COPIED_MS);
		return () => window.clearTimeout(timer);
	}, [copyResult]);

	const status = getCopilotConsentStatus(tenant?.copilotConsentState);
	const checkedAt = parseCopilotCheckedAt(tenant?.copilotConsentCheckedAt);
	// The page the Grant button opens. A partner sends it to the customer's administrator, and
	// Consent.tsx exchanges it for Microsoft's admin-consent URL with the seven Copilot scopes.
	const consentLink = adminConsentPageUrl(window.location.origin, tenant?.tenantId, ScopeType.Copilot);

	const statusText = status === 'granted'
		? (checkedAt
			? languageStrings.CopilotConsentGranted.replace('{date}', checkedAt.toLocaleDateString())
			: languageStrings.CopilotConsentGrantedUndated)
		: status === 'missing'
			? languageStrings.CopilotConsentMissing
			: languageStrings.CopilotConsentUnknown;

	const copyLink = async () => {
		const copied = await copyToClipboard(consentLink, sectionRef.current ?? document.body);
		setCopyResult(copied ? 'copied' : 'failed');
		// The copy command selects its text area; give focus back to the button that was pressed.
		copyButtonRef.current?.focus();
	};

	const isCopied = copyResult === 'copied';

	return (
		<div ref={sectionRef}>
			<Stack tokens={{ childrenGap: 8 }}>
				<Label styles={labelHeadingStyle}>{languageStrings.Copilot}</Label>
				<MessageBar messageBarType={statusBarTypes[status]}>{statusText}</MessageBar>
				<Text variant="small" styles={{ root: mutedText }}>
					{languageStrings.CopilotConsentStatusHint}
				</Text>
				<Stack horizontal wrap tokens={{ childrenGap: 8 }} styles={{ root: { paddingTop: 4 } }}>
					<DefaultButton
						onClick={() => openAdminConsentPage(tenant?.tenantId, ScopeType.Copilot)}
					>
						{languageStrings.CopilotGrantConsent}
					</DefaultButton>
					{/* An icon button (Paul, 2026-10-16): the tooltip shows what the link is for, or that it was
					    copied; screen readers get the button's name and the hint as its description. */}
					<TooltipHost content={isCopied ? languageStrings.CopilotLinkCopied : languageStrings.CopilotCopyLinkHint} setAriaDescribedBy={false}>
						<IconButton
							iconProps={{ iconName: isCopied ? 'CheckMark' : 'Link' }}
							ariaLabel={isCopied ? languageStrings.CopilotLinkCopied : languageStrings.CopilotCopyLink}
							ariaDescription={languageStrings.CopilotCopyLinkHint}
							elementRef={copyButtonRef}
							onClick={copyLink}
						/>
					</TooltipHost>
				</Stack>
				{copyResult === 'failed' && (
					// Neither the Clipboard API nor the copy command worked: show the link to copy by hand.
					<MessageBar messageBarType={MessageBarType.warning} onDismiss={() => setCopyResult(undefined)}>
						{languageStrings.CopilotCopyLinkFailed}{' '}
						<span style={{ userSelect: 'all', wordBreak: 'break-all' }}>{consentLink}</span>
					</MessageBar>
				)}
				<ActionButton
					iconProps={{ iconName: isDetailsOpen ? 'ChevronDown' : 'ChevronRight' }}
					aria-expanded={isDetailsOpen}
					aria-controls={detailsId}
					onClick={() => setIsDetailsOpen(open => !open)}
					styles={detailsToggleStyles}
				>
					{languageStrings.CopilotAboutHeading}
				</ActionButton>
				{isDetailsOpen && (
					<Stack id={detailsId} tokens={{ childrenGap: 8 }} styles={{ root: { paddingLeft: 16 } }}>
						<ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, ...mutedText }}>
							<li>{languageStrings.CopilotAboutLicence}</li>
							<li>{languageStrings.CopilotAboutPermissions}</li>
							<li>{languageStrings.CopilotAboutEveryUser}</li>
							<li>{languageStrings.CopilotAboutPrivacy}</li>
							<li>{languageStrings.CopilotAboutRevoke}</li>
						</ul>
					</Stack>
				)}
				{/* Phase 3: the tenant's Copilot prompts. A tenant that is not saved yet has no Id. */}
				<DefaultButton
					text={languageStrings.ManageCopilotPrompts}
					disabled={!tenant?.id || tenant.id <= 0}
					onClick={() => setIsPromptsPanelOpen(true)}
					styles={{ root: { alignSelf: 'flex-start', marginTop: 8 } }}
				/>
				{isPromptsPanelOpen && (
					<CopilotPromptsPanel
						isOpen
						tenantId={tenant.id}
						tenantName={tenant.friendlyName}
						onClose={() => setIsPromptsPanelOpen(false)}
					/>
				)}
			</Stack>
		</div>
	);
};

export default CopilotSettingsSection;
